import { useDraggable, useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import type { IssueStatus, PMIssue } from "@/domains/pm/types";
import type { ViewerDocumentData } from "@/domains/documents/hooks/useDocumentViewer";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { KanbanCard, KanbanDraftCard } from "./KanbanCard";
import { useDraftIssueView, type DraftIssueViewProps } from "./issueDraftView";
import { cn } from "@/lib/utils";

interface KanbanColumnProps extends DraftIssueViewProps {
  status: IssueStatus;
  label: string;
  color: string;
  /** In display order — the board has already applied the arrangement. */
  issues: PMIssue[];
  /** The same cards' ids. Kept by the board as the same array while the column
   *  is unchanged: it is the SortableContext's items, and a new array there
   *  re-renders every card in the column. */
  cardIds: number[];
  /** Whether the user may pick this card up. */
  canDragCard: (issue: PMIssue) => boolean;
  /** A drag the user may not make — refused with a message. */
  onBlockedDrag?: (issue: PMIssue) => void;
  buildingShortCode?: string;
  openViewer: (doc: ViewerDocumentData) => Promise<void>;
  onSelectIssue: (issue: PMIssue) => void;
  /** Open the New Issue form pre-set to this column's status. */
  onAddIssue: (status: IssueStatus) => void;
  /** Custom columns can be dragged sideways; the three built-ins keep their
   *  relative order and so have no handle. */
  reorderable?: boolean;
}

export function KanbanColumn({
  status,
  label,
  color,
  issues,
  cardIds,
  canDragCard,
  onBlockedDrag,
  buildingShortCode,
  openViewer,
  onSelectIssue,
  onAddIssue,
  reorderable = false,
  ...draftProps
}: KanbanColumnProps) {
  // `draftIssues` here are only the drafts filed in this column.
  const drafts = useDraftIssueView(draftProps);
  // Three ids, and all three must DIFFER. A card lands on the body, a column on
  // the header, and the header is also what gets dragged — giving the header
  // droppable the same id as the draggable made dnd-kit resolve no `over` at
  // all, so a column drop silently did nothing.
  const { setNodeRef, isOver } = useDroppable({ id: status, data: { kind: "cards", status } });
  const header = useDroppable({ id: `colhead:${status}`, data: { kind: "column", status } });
  const drag = useDraggable({
    id: `col:${status}`,
    disabled: !reorderable,
    data: { kind: "column", status },
  });
  const { t } = useLanguage();

  return (
    // The WHOLE column is the draggable node, so it travels with the cursor the
    // way a card does. Dragging only faded the header by 40% and moved nothing,
    // which read as the gesture not having started at all.
    <div
      ref={drag.setNodeRef}
      style={{ transform: CSS.Translate.toString(drag.transform) }}
      className={cn(
        "flex w-72 shrink-0 flex-col transition-shadow",
        drag.isDragging && "z-30 rotate-1 opacity-90 shadow-xl"
      )}
    >
      {/* Column header. Also the column's own drop target and drag handle. */}
      <div
        ref={header.setNodeRef}
        className={cn(
          "group/col mb-3 flex items-center gap-2 rounded px-1 py-0.5 transition-colors",
          // Where it would land. Only ever another column's header — a column
          // is never a drop target for itself.
          header.isOver && !drag.isDragging && "bg-primary/10 ring-1 ring-primary/30"
        )}
      >
        {reorderable && (
          <button
            type="button"
            ref={drag.setActivatorNodeRef}
            aria-label={t.pm.issues.statuses.reorderColumn}
            className={cn(
              "-ml-1 shrink-0 touch-none rounded p-0.5 text-muted-foreground transition-opacity hover:bg-muted focus-visible:opacity-100",
              drag.isDragging
                ? "cursor-grabbing opacity-100"
                : "cursor-grab opacity-0 group-hover/col:opacity-100"
            )}
            {...drag.listeners}
            {...drag.attributes}
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
        )}
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
        <span className="text-sm font-medium">{label}</span>
        <span className="ml-auto inline-flex h-[16px] min-w-[18px] items-center justify-center rounded-full bg-muted px-1.5 text-[10px] font-semibold text-muted-foreground">
          {issues.length}
        </span>
      </div>

      {/* Droppable body */}
      <div
        ref={setNodeRef}
        className={cn(
          "flex min-h-[120px] flex-1 flex-col gap-2 rounded-lg p-2 transition-colors",
          isOver && "bg-primary/5 ring-1 ring-primary/20"
        )}
      >
        {/* Drafts first, and OUTSIDE the SortableContext: a draft is not
            sortable, is never a drop target, and never enters `cardIds` —
            the order the board stores for the column. */}
        {drafts.drafts.map((draft) => (
          <KanbanDraftCard
            key={draft.id}
            issue={draft}
            isActive={drafts.isActive(draft.id)}
            isSaving={drafts.isSaving(draft.id)}
            onSelect={drafts.select}
            openViewer={openViewer}
          />
        ))}
        <SortableContext items={cardIds} strategy={verticalListSortingStrategy}>
          {issues.map((issue) => (
            <KanbanCard
              key={issue.id}
              issue={issue}
              buildingShortCode={buildingShortCode}
              openViewer={openViewer}
              onSelect={onSelectIssue}
              draggable={canDragCard(issue)}
              onBlockedDrag={onBlockedDrag}
            />
          ))}
        </SortableContext>

        {/* Add-issue CTA — last child inside the droppable body so it sits
            flush below the last card (or directly under the header when the
            column is empty). Clicking opens the New Issue form with this
            column's status pre-selected. */}
        <button
          type="button"
          onClick={() => onAddIssue(status)}
          className="flex w-full items-center justify-center gap-1 rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary/70"
        >
          + {t.pm.issues.addIssue}
        </button>
      </div>
    </div>
  );
}
